// Sections
import { CognitiveRouteMeta, CognitiveRouteRecord, DeepExtractNames, DeepExtractTitles } from '@/router/routes/types'
import admin from './admin'
import company from './company'
import { useWatchlistsStore } from '@/piniaStores/watchlists/watchlistsStore'
import { RouteLocationMatched, RouteLocationNormalizedLoaded } from 'vue-router'

const routes = [
  {
    path: '/excel-auth',
    name: 'Excel Add-In Authentication',
    component: () => import('@/pages/ExcelAuth.vue'),
    meta: {
      title: 'Cognitive Credit',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/activate',
    name: 'Activate User',
    component: () => import('@/pages/ActivateUser.vue'),
    meta: {
      title: 'Cognitive Credit',
      requiredRoles: [],
      token: true,
    },
  },
  {
    path: '/',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
    alias: '/index.html',
    meta: {
      title: 'Cognitive Credit Login',
      requiredRoles: [],
    },
  },
  {
    path: '/oauth',
    name: 'OAuth Login',
    component: () => import('@/pages/OAuthLogin.vue'),
    meta: {
      title: 'Cognitive Credit Login',
      requiredRoles: [],
    },
  },
  {
    path: '/oauth/authorize',
    name: 'OAuth Consent',
    component: () => import('@/pages/OAuthConsent.vue'),
    meta: {
      title: 'Authorise — Cognitive Credit',
      requiredRoles: [],
    },
  },
  {
    path: '/oauth/callback',
    name: 'OAuth Callback',
    component: () => import('@/pages/OAuthCallback.vue'),
    meta: {
      title: 'Cognitive Credit',
      requiredRoles: [],
    },
  },
  {
    path: '/memogen',
    name: 'Memo Generator',
    component: () => import('@/pages/MemoGen.vue'),
    meta: {
      title: 'memo-generator',
      requiredRoles: [],
    },
  },
  {
    path: '/memo',
    name: 'Memo',
    component: () => import('@/pages/MemoEmail.vue'),
    meta: {
      title: 'memo',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/inspector',
    name: 'Data Inspector',
    component: () => import('@/components/admin/documents/editor/HTML/HTMLInspector.vue'),
    meta: {
      title: 'data-inspector',
      requiredRoles: [],
    },
  },
  {
    path: '/html',
    name: 'HTML View',
    component: () => import('@/pages/HtmlPage.vue'),
    meta: {
      title: 'html-view',
      requiredRoles: [],
    },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/pages/404Page.vue'),
  },
  {
    path: '/not-allowed',
    name: 'NotAllowed',
    component: () => import('@/pages/NotAllowed.vue'),
    meta: {
      title: 'Not Allowed',
      requiredRoles: [],
    },
  },
  {
    path: '/activation-error',
    name: 'Error',
    component: () => import('@/pages/ActivationErrorPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'Catch All',
    component: () => import('@/pages/404Page.vue'),
  },
  {
    path: '/text-search',
    name: 'Text Search',
    component: () => import('@/pages/TextSearch.vue'),
    meta: {
      title: 'Text Search',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/drive/:scope?',
    name: 'Drive',
    component: () => import('@/pages/DrivePage.vue'),
    meta: {
      title: 'Drive',
      requiredRoles: ['user'],
    },
    children: [
      {
        path: 'file/:fileId/rename',
        name: 'Drive File Rename Modal',
        component: () => import('@/components/drive/DriveModals.vue'),
        meta: {
          title: 'Drive - Rename File',
          requiredRoles: ['user'],
        },
      },
      {
        path: ':fileId/rename',
        name: 'Drive Rename Modal',
        component: () => import('@/components/drive/DriveModals.vue'),
        meta: {
          title: 'Drive - Rename FIle',
          requiredRoles: ['user'],
        },
      },
    ],
  },
  {
    path: '/drive/note/new',
    name: 'Drive Note Modal New',
    component: () => import('@/components/drive/DriveNotes.vue'),
    meta: {
      title: 'Drive - New Note',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/drive/note/:noteId/edit',
    name: 'Drive Note Modal Edit',
    component: () => import('@/components/drive/DriveNotes.vue'),
    meta: {
      title: 'Drive - Edit Note',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/drive/note/:noteId',
    name: 'Drive View Note Modal',
    component: () => import('@/components/drive/DriveNotes.vue'),
    meta: {
      title: 'Drive - View Note',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/companies',
    name: 'Companies',
    component: () => import('@/pages/CompaniesPage.vue'),
    meta: {
      title: 'Companies',
      requiredRoles: ['user'],
    },
    children: [
      {
        path: 'list',
        alias: '',
        component: () => import('@/components/companies/CompanyList/CompanyList.vue'),
        meta: {
          title: 'Companies',
          requiredRoles: ['user'],
        },
      },
      {
        path: 'request-coverage',
        name: 'Request Coverage',
        meta: {
          title: 'Request Coverage',
          requiredRoles: ['user'],
        },
        component: () => import('@/components/companies/CompaniesRequestCoverage/CompaniesRequestCoverage.vue'),
      },
    ],
  },
  {
    path: '/comparables',
    name: 'Comparables',
    component: () => import('@/pages/Comparables.vue'),
    redirect: '/comparables/company',
    meta: {
      title: 'Company Comparables',
      requiredRoles: ['user'],
    },
    children: [
      {
        path: 'company',
        name: 'Company Comparables',
        component: () => import('@/pages/comparables/Company.vue'),
        props: (route) => ({
          companyId: route.query.id,
        }),
        meta: {
          title: 'Company Comparables',
          requiredRoles: ['user'],
          hasSpreadsheet: true,
        },
      },
      {
        path: 'market',
        name: 'Market Comparables',
        component: () => import('@/pages/comparables/Market.vue'),
        props: (route) => ({
          companyId: route.query.id,
        }),
        meta: {
          title: 'Market Comparables',
          requiredRoles: ['user'],
          hasSpreadsheet: true,
        },
      },
      {
        path: 'sector',
        name: 'Sector Comparables',
        component: () => import('@/pages/comparables/Sector.vue'),
        props: (route) => ({
          companyId: route.query.id,
        }),
        meta: {
          title: 'Sector Comparables',
          requiredRoles: ['user'],
          hasSpreadsheet: true,
        },
        /**
         * TODO: Review this logic at later stage
         * The Selection of universes and the watch list logic should be consistent across
         * all the areas of the App, however the current Comps approach to display watch list
         * companies doesn't permit it so the app need to hack it by forcing the user to
         * download the whole list of comps across all the universes to then filter within the UI
         */
        beforeEnter: () => {
          const watchlistStore = useWatchlistsStore()
          if (watchlistStore.anyWatchlistActive) {
            watchlistStore.disableWatchlists()
          }
        },
      },
      {
        path: 'movers',
        name: 'Market Movers',
        component: () => import('@/pages/comparables/MarketMovers.vue'),
        props: (route) => ({
          companyId: route.query.id,
        }),
        meta: {
          title: 'Market Movers',
          requiredRoles: ['user'],
          hasSpreadsheet: true,
        },
      },
    ],
  },
  {
    path: '/profile',
    component: () => import('@/pages/ProfilePage.vue'),
    redirect: '/profile/preferences',
    meta: {
      title: 'Profile',
      requiredRoles: ['user'],
    },
    children: [
      {
        path: 'my-team',
        component: () => import('@/pages/profile/MyTeam.vue'),
        meta: {
          title: 'Profile',
          requiredRoles: ['user'],
        },
      },
      {
        path: 'preferences',
        component: () => import('@/pages/profile/Preferences.vue'),
        meta: {
          title: 'Profile',
          requiredRoles: ['user'],
        },
      },
    ],
  },
  {
    path: '/help',
    component: () => import('@/pages/help/HelpPage.vue'),
    redirect: '/help/update-financials',
    meta: {
      title: 'Help',
      requiredRoles: ['user'],
    },
    children: [
      {
        path: 'update-financials',
        component: () => import('@/pages/help/UpdateFinancials.vue'),
        meta: {
          title: 'Help - Update Financials',
          requiredRoles: ['user'],
        },
      },
    ],
  },
  // TODO rm page route
  {
    path: '/document/:extension/:id/:page?',
    component: () => import('@/pages/DocumentPage.vue'),
    meta: {
      title: 'Document',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/document/rac/:extension/:id/:page?',
    component: () => import('@/pages/RACDocumentPage.vue'),
    meta: {
      title: 'Document',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/ai',
    name: 'Cognitive Credit AI',
    component: () => import('@/pages/ChatApp.vue'),
    meta: {
      title: 'Cognitive Credit AI',
      requiredRoles: ['user'],
    },
  },
  {
    path: '/dealconnect',
    name: 'DealConnect',
    component: () => import('@/pages/DealConnectPage.vue'),
    meta: {
      title: 'DealConnect',
      requiredRoles: ['user'],
    },
  },
] as const satisfies CognitiveRouteRecord[]

const routing: CognitiveRouteRecord[] = [
  ...(routes as CognitiveRouteRecord[]),
  ...(admin as CognitiveRouteRecord[]),
  ...(company as CognitiveRouteRecord[]),
]

export type CognitiveRouteNames =
  | DeepExtractNames<typeof routes>
  | DeepExtractNames<typeof admin>
  | DeepExtractNames<typeof company>

export type CognitiveRouteTitles =
  | DeepExtractTitles<typeof routes>
  | DeepExtractTitles<typeof admin>
  | DeepExtractTitles<typeof company>

/** This interface extends CognitiveRouteMeta to enforce typing on
 * the title property. Using CognitiveRouteMeta would not offer type safety
 * on the title since it is a string,
 * but by intersecting it with a type that extracts only the valid
 * titles from our route definitions, we can ensure that any
 * usage of CognitiveRouteLocation will have a title that is
 * guaranteed to be one of the defined route titles.
 */
interface TypedRouteMeta extends CognitiveRouteMeta {
  title: Extract<CognitiveRouteTitles, string>
}

/** This is the equivalent of CognitiveRouteRecord but for route
 * locations, i.e. the objects that represent the current route
 * in navigation guards and components.
 */
export type CognitiveRouteLocation = Omit<RouteLocationNormalizedLoaded, 'meta' | 'name' | 'matched'> & {
  name: Extract<CognitiveRouteNames, string> | null | undefined
  meta: TypedRouteMeta
  matched: (Omit<RouteLocationMatched, 'meta'> & { meta: TypedRouteMeta })[]
}

export default routing
